'use client';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

import ImageWithFallback from '@/components/ImageWithFallback/ImageWithFallback';
import { Gear } from '@/modules/players/v3/types';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';

interface GearImageCarouselProps {
  images: Gear['images'];
  alt: string;
  className?: string;
  hideBadge?: boolean;
}

export const GearImageCarousel = ({
  images,
  alt,
  className,
  hideBadge = false,
}: GearImageCarouselProps) => {
  const sortedImages = useMemo(
    () => [...images].sort((a, b) => a.sortOrder - b.sortOrder),
    [images]
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasMultiple = sortedImages.length > 1;
  const fallbackSrc = getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 256, 256);
  const current = sortedImages[currentIndex];

  const goTo = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(Math.max(0, Math.min(sortedImages.length - 1, index)));
  };

  return (
    <div className={clsx('relative flex items-center justify-center overflow-hidden', className)}>
      <ImageWithFallback
        src={current?.urlThumbnail}
        fallbackSrc={fallbackSrc}
        alt={alt}
        className='size-full object-contain'
      />

      {hasMultiple ? (
        <>
          {!hideBadge ? (
            <span className='absolute right-1 top-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium leading-none text-gray-700'>
              {`${currentIndex + 1}/${sortedImages.length}`}
            </span>
          ) : null}
          {currentIndex > 0 ? (
            <button
              type='button'
              aria-label='Previous image'
              onClick={(e) => goTo(currentIndex - 1, e)}
              className='absolute left-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-sm'
            >
              <FontAwesomeIcon icon={faChevronLeft} className='size-2.5 text-gray-700' />
            </button>
          ) : null}
          {currentIndex < sortedImages.length - 1 ? (
            <button
              type='button'
              aria-label='Next image'
              onClick={(e) => goTo(currentIndex + 1, e)}
              className='absolute right-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-sm'
            >
              <FontAwesomeIcon icon={faChevronRight} className='size-2.5 text-gray-700' />
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
